import { useState } from 'react'
// import { Link } from 'react-router-dom'  // used only by the commented-out Teams tab
import { useTranslation } from 'react-i18next'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { FacetTile } from '@/components/forge/FacetTile'
import { ForgeButton, IconButton } from '@/components/forge/ForgeButton'
import { ForgeDialog } from '@/components/forge/ForgeDialog'
import { ForgeSelect } from '@/components/forge/ForgeSelect'
import { Icon } from '@/components/forge/Icon'
import { Panel } from '@/components/forge/Panel'
import { PAGE_SIZES, TablePagination } from '@/components/forge/TablePagination'
import { EmptyState, ErrorState, SkeletonRows } from '@/components/forge/States'
import { Mono, Num } from '@/components/forge/primitives'
import { usePermission } from '@/features/auth/permissions'
import { initialsOf } from '@/features/users/api'
import { useDeleteUser, useSetUserActive, useUsers } from '@/features/users/queries'
// The demo directory, which this screen no longer reads. Kept for the two tabs
// commented out at the foot of this file.
// import {
//   PERSON_FACETS,
//   TEAM_TYPES,
//   usePeople,
//   usePeopleImports,
//   useSetPersonStatus,
//   useTeams,
// } from '@/features/people/queries'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useToast } from '@/providers/ToastProvider'
import { downloadCsv, toCsv } from '@/lib/csv'
import { formatInteger, formatRelative } from '@/lib/format'
import { ON_TINT_CLASS, STRIPE_CLASS, TINT_CLASS } from '@/lib/tone'
import { cn } from '@/lib/cn'
import { UserEditorDrawer } from './UserEditorDrawer'
// import { CreatePersonDialog } from './CreatePersonDialog'
// import { PeopleImportWizard } from './PeopleImportWizard'

/**
 * Settings → Users & Teams.
 *
 * LIVE, against `/v1/users`. The demo screen this replaces is in the git history
 * and, where a control had no endpoint behind it, commented out in place below.
 *
 * ── One tab where there were three ───────────────────────────────────────
 * §8.14 recorded that shipping Users without Teams and Import History was
 * wrong, because "a slice of it reads as a screen somebody forgot to finish
 * rather than as a decision". That still holds for a demo of a design. It does
 * not hold here: teams and bulk import have no API at all, and the honest thing
 * is one tab that is real rather than three where two are synthetic. A tab strip
 * with one tab in it is furniture, so it goes too — both tabs and the strip are
 * commented out at the foot of this file, and restoring them when the endpoints
 * land is uncommenting, not rebuilding.
 *
 * ── Two states, not three ────────────────────────────────────────────────
 * `is_active` is a boolean. The demo's third state — locked, meaning the server
 * shut somebody out after failed sign-ins — is not a fact this API records, so
 * the ladder is Active and Deactivated and the Lock action is gone. Inventing a
 * third pill would describe an enforcement that does not happen.
 *
 * ── The columns are what the API serves ──────────────────────────────────
 * A name, an address, whether the account may sign in, when it was last used and
 * when it was made. MFA, home site, sites-reached and team are all commented out
 * on the row: the first two have no field, and the last two have no resource.
 * §8.14's derived "last active" — the end of somebody's most recent finished
 * shift — is now the server's `last_active_at`, stamped on sign-in and on every
 * token refresh, which is a better answer to the same question.
 *
 * **"Never" is still the useful answer**, and it means more here than it did:
 * an invited account nobody has accepted has never been active, so the column
 * that shows a dormant account also shows an unaccepted invitation.
 *
 * ── Ordered by name, not as an access review ─────────────────────────────
 * §8.14 ordered the demo as an access review — locked, then dormant, then no
 * second factor, then everybody else — over a list it held whole. The server
 * pages and sorts, and it sorts by `created_at` or `first_name` and nothing
 * else. Sorting a page locally would order the page rather than the directory
 * and quietly disagree with itself on page two, so the order offered is the
 * order the server can actually produce. `last_active_at` is the column a review
 * would sort by and is not one of them — recorded in BACKEND-GAPS.md.
 *
 * ── The root account is shown and cannot be touched ──────────────────────
 * `GET /users` lists it; detail, update and delete all refuse it with a 403,
 * because a root user is managed through their own profile. So the row renders
 * with a badge saying why and no menu — offering an action the server will
 * refuse and explaining afterwards is worse than not offering it.
 */

/** `null` means both. The tiles are the `is_active` axis and there is no other. */
const ACTIVE_FACETS = [
  { id: 'all', value: null, tone: null, icon: 'users' },
  { id: 'active', value: true, tone: 'green', icon: 'check' },
  /* `minus` and not a lock: the sprite has a lock and this is not one. A
     deactivated account was switched off by an administrator, which is a
     different fact from the server having shut somebody out — and that second
     fact is one this API does not record at all. */
  { id: 'inactive', value: false, tone: null, icon: 'minus' },
]

/**
 * The sorts the server actually serves, spelled as one control.
 *
 * `sort_by` is `created_at` or `first_name`; `sort_dir` is asc or desc. Four
 * combinations, offered as four options rather than as two selects, because
 * "sort by name, descending" is one decision and two controls for it invites the
 * pair that makes no sense to anybody.
 */
/** One of the footer's sizes, so the two cannot disagree about what is offered. */
const DEFAULT_PAGE_SIZE = PAGE_SIZES.includes(50) ? 50 : PAGE_SIZES[0]

const SORTS = [
  { id: 'nameAsc', sortBy: 'first_name', sortDir: 'asc' },
  { id: 'nameDesc', sortBy: 'first_name', sortDir: 'desc' },
  { id: 'newest', sortBy: 'created_at', sortDir: 'desc' },
  { id: 'oldest', sortBy: 'created_at', sortDir: 'asc' },
]

export function UsersPage() {
  const { t } = useTranslation(['app', 'common'])
  useDocumentTitle(`${t('settings.users.title')} · ${t('common:brand.name')}`)

  /**
   * Everything on this screen is behind one permission, so the gate is at the
   * page and not on each control. `is_root` bypasses it, as it does server-side.
   *
   * This is not the authorisation — the API checks every request — it is what
   * keeps somebody from being shown a directory that will answer 403 to
   * everything they do with it.
   */
  const gate = usePermission('users.manage')

  return (
    <div className="px-8 py-6 max-xl:px-5.5">
      <header className="mb-4">
        <h1 className="m-0 text-headline">{t('settings.users.title')}</h1>
        <p className="mt-2 mb-0 max-w-[72ch] text-body-sm text-ink-dim">
          {t('settings.users.intro')}
        </p>
      </header>

      {/* The tab strip is commented out with the two tabs it switched between —
          see the foot of this file. */}

      {gate.isPending ? (
        <SkeletonRows count={5} />
      ) : gate.granted ? (
        <Directory />
      ) : (
        <EmptyState
          icon="lock"
          title={t('settings.users.forbidden')}
          body={t('settings.users.forbiddenBody')}
        />
      )}
    </div>
  )
}

function Directory() {
  const { t, i18n } = useTranslation(['app', 'common'])
  const { toast } = useToast()

  const [facet, setFacet] = useState('all')
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState(SORTS[0].id)
  /* 50 from the footer's own ladder rather than a second list of sizes here —
     and it is inside the server's `page_size` ceiling of 200, which is also
     where that ladder stops. */
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  /**
   * The page, DERIVED against the filters it was set for.
   *
   * Stamped rather than reset with an effect, which is the pattern Sites &
   * Hierarchy settled on: narrowing a list while standing on page four would
   * otherwise ask the server for a page that no longer exists and answer with an
   * empty table that looks like "no matches".
   */
  const [pageState, setPageState] = useState({ key: '', value: 1 })
  const filterKey = `${facet}|${query.trim()}|${sort}|${pageSize}`
  const page = pageState.key === filterKey ? pageState.value : 1
  const setPage = (value) => setPageState({ key: filterKey, value })

  /** `{ mode: 'create' } | { mode: 'edit', id }` — one editor at a time. */
  const [editor, setEditor] = useState(null)
  /** The row a confirmation is open for. */
  const [removing, setRemoving] = useState(null)
  /** The row a deactivation is being confirmed for. Reactivating is not asked —
      it gives access back, and a mistake there is undone the same way. */
  const [deactivating, setDeactivating] = useState(null)

  const chosenSort = SORTS.find((entry) => entry.id === sort) ?? SORTS[0]
  const chosenFacet = ACTIVE_FACETS.find((entry) => entry.id === facet) ?? ACTIVE_FACETS[0]

  const users = useUsers({
    isActive: chosenFacet.value ?? undefined,
    search: query.trim() || undefined,
    sortBy: chosenSort.sortBy,
    sortDir: chosenSort.sortDir,
    page,
    pageSize,
  })

  const setActive = useSetUserActive()
  const remove = useDeleteUser()

  function applyActive(person, isActive, onDone) {
    setActive.mutate(
      { id: person.id, isActive },
      {
        onSuccess: () => {
          toast(
            t(isActive ? 'settings.users.activated' : 'settings.users.deactivated', {
              name: person.name,
            }),
            'ok',
          )
          onDone?.()
        },
        onError: () => toast(t('settings.users.statusFailed'), 'error'),
      },
    )
  }

  const rows = users.data?.items ?? []
  const counts = users.data?.counts ?? { active: 0, inactive: 0 }
  /* The server counts both sides of the split over the SEARCHED set, so the
     total is their sum rather than a third figure — and it narrows with a query
     while staying still when the filter picks a side, which is what makes these
     three tiles a count rather than a page tally. */
  const countFor = { all: counts.active + counts.inactive, active: counts.active, inactive: counts.inactive }
  const total = users.data?.total ?? 0

  const narrowed = facet !== 'all' || query.trim().length > 0

  function clearFilters() {
    setFacet('all')
    setQuery('')
  }

  /* Exports what is on the screen, not the whole directory — a filtered table
     and an export that ignored the filter are two answers to one question, and
     the reader finds out only after opening the file. It is one PAGE of the
     directory now rather than all of it, which is a real limit and is why the
     button names the count it is about to write. */
  function exportCsv() {
    downloadCsv(
      'users.csv',
      toCsv(
        ['name', 'email', 'status', 'lastActive', 'created'],
        rows.map((person) => [
          person.name,
          person.email,
          person.isActive ? 'active' : 'inactive',
          person.lastActiveAt ?? '',
          person.createdAt ?? '',
        ]),
      ),
    )
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-2.5">
        <h2 className="m-0 text-title">{t('settings.users.management')}</h2>
        <div className="flex flex-wrap gap-2">
          {/* Import is commented out: `/users/import` is not an endpoint the API
              serves, and a wizard reporting accounts it created would be
              describing something that did not happen.
              <ForgeButton onClick={() => setImporting(true)}>
                <Icon name="up" size="xs" />
                {t('settings.users.import')}
              </ForgeButton> */}
          <ForgeButton disabled={!rows.length} onClick={exportCsv}>
            <Icon name="down" size="xs" />
            {t('settings.users.exportPage', { count: rows.length })}
          </ForgeButton>
          <ForgeButton variant="primary" onClick={() => setEditor({ mode: 'create' })}>
            {t('settings.users.invite')}
          </ForgeButton>
        </div>
      </div>

      {users.isPending ? (
        <SkeletonRows count={5} />
      ) : users.isError ? (
        <ErrorState
          error={users.error}
          title={t('settings.users.loadFailed')}
          onRetry={users.refetch}
        />
      ) : (
        <>
          {/* Three tiles where there were seven. The other four counted MFA
              state and never-signed-in, neither of which the API can filter on:
              `is_active` is the only axis `GET /users` offers, and a tile that
              cannot narrow the table is a decoration. See BACKEND-GAPS.md. */}
          <div className="mb-3.5 grid grid-cols-3 gap-2.5 max-lg:grid-cols-1">
            {ACTIVE_FACETS.map((entry) => (
              <FacetTile
                key={entry.id}
                icon={entry.icon}
                tone={entry.tone}
                label={t(`settings.users.facets.${entry.id}`)}
                caption={t(`settings.users.facetHints.${entry.id}`)}
                value={formatInteger(countFor[entry.id] ?? 0)}
                selected={facet === entry.id}
                onClick={() => setFacet(entry.id)}
              />
            ))}
          </div>

          <Panel flush className="mb-3.5 px-3.5 py-2.5">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
              <ForgeSelect
                id="users-sort"
                labelHidden
                size="sm"
                label={t('settings.users.sortLabel')}
                value={sort}
                onValueChange={setSort}
                className="w-[15rem] shrink-0"
                options={SORTS.map((entry) => ({
                  value: entry.id,
                  label: t(`settings.users.sorts.${entry.id}`),
                }))}
              />

              {/* The role and site filters are commented out with the columns
                  they narrowed: `GET /users` takes `is_active` and `search` and
                  nothing else, and filtering a page in the client would search
                  what happens to be on screen. */}

              <div className="ms-auto flex min-w-[13rem] items-center gap-2 rounded-sm border border-hairline bg-card px-3 py-1.5 focus-within:border-forge">
                <Icon name="search" size="sm" className="text-ink-mute" />
                <label className="sr-only" htmlFor="users-search">
                  {t('settings.users.searchLabel')}
                </label>
                <input
                  id="users-search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t('settings.users.searchPlaceholder')}
                  className="min-w-0 flex-1 border-0 bg-transparent text-body-sm text-ink outline-none placeholder:text-ink-dim"
                />
              </div>

              <p className="m-0 text-body-sm whitespace-nowrap text-ink-dim">
                <Num>
                  {t('settings.users.matchCount', {
                    shown: formatInteger(rows.length),
                    total: formatInteger(total),
                  })}
                </Num>
              </p>
            </div>
          </Panel>

          {/* The checkbox column and its bulk deactivate are commented out with
              the selection: bulk deactivate is one PUT per row, each needing its
              own read first to avoid revoking the grants it is not changing —
              see `setUserActive`. Twenty rows is forty requests with no
              transaction around them, and a partial failure leaves the table
              disagreeing with itself. A bulk endpoint would fix it; there is
              none. Recorded in BACKEND-GAPS.md. */}

          {rows.length ? (
            <Panel flush className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <caption className="sr-only">{t('settings.users.tableCaption')}</caption>
                  <thead>
                    <tr>
                      {['person', 'status', 'lastActive', 'created'].map((column) => (
                        <th
                          key={column}
                          scope="col"
                          className="eyebrow bg-sunken px-3.5 py-2.5 text-start whitespace-nowrap"
                        >
                          {t(`settings.users.columns.${column}`)}
                        </th>
                      ))}
                      <th scope="col" className="bg-sunken px-3.5 py-2.5">
                        <span className="sr-only">{t('settings.users.columns.actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((person) => (
                      <tr
                        key={person.id}
                        className={cn(
                          'border-t border-subtle hover:bg-sunken',
                          /* The earned leading edge, on the one row state
                             somebody has to do something about. The demo also
                             striped an active account with no second factor;
                             there is no such field. */
                          !person.isActive && STRIPE_CLASS.amber,
                        )}
                      >
                        <td className="px-3.5 py-2.5">
                          <span className="flex items-center gap-2.5">
                            <span
                              aria-hidden="true"
                              className="flex size-8 shrink-0 items-center justify-center rounded-full border border-hairline bg-sunken text-label font-bold text-ink-dim"
                            >
                              {initialsOf(person)}
                            </span>
                            <span className="flex min-w-0 flex-col">
                              <span className="flex items-center gap-2">
                                <span
                                  className="text-body-sm font-semibold text-ink"
                                  translate="no"
                                >
                                  {person.name}
                                </span>
                                {person.isRoot ? <RootBadge /> : null}
                              </span>
                              <Mono className="text-micro text-ink-dim">{person.email}</Mono>
                            </span>
                          </span>
                        </td>
                        <td className="px-3.5 py-2.5">
                          <StatusPill active={person.isActive} />
                        </td>
                        <td className="px-3.5 py-2.5 text-body-sm whitespace-nowrap text-ink-dim">
                          {person.lastActiveAt ? (
                            <time dateTime={person.lastActiveAt}>
                              {formatRelative(new Date(person.lastActiveAt), i18n.language)}
                            </time>
                          ) : (
                            /* Never used, which for an account made minutes ago
                               means an invitation nobody has accepted yet. */
                            <span className="text-on-amber">{t('settings.users.never')}</span>
                          )}
                        </td>
                        <td className="px-3.5 py-2.5 text-body-sm whitespace-nowrap text-ink-dim">
                          {person.createdAt ? (
                            <time dateTime={person.createdAt}>
                              {formatRelative(new Date(person.createdAt), i18n.language)}
                            </time>
                          ) : (
                            <span className="text-ink-mute">—</span>
                          )}
                        </td>
                        <td className="px-3.5 py-2.5 text-end">
                          {/* No menu on the root account. Every item on it is a
                              route that answers 403 for that row. */}
                          {person.isRoot ? null : (
                            <RowMenu
                              person={person}
                              pending={setActive.isPending}
                              onEdit={() => setEditor({ mode: 'edit', id: person.id })}
                              onSetActive={(isActive) =>
                                isActive ? applyActive(person, true) : setDeactivating(person)
                              }
                              onDelete={() => setRemoving(person)}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <TablePagination
                total={total}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                label={t('settings.users.rowNoun')}
              />
            </Panel>
          ) : (
            <EmptyState
              icon="users"
              title={t('settings.users.noMatch')}
              body={t('settings.users.noMatchBody')}
              action={
                narrowed ? (
                  <ForgeButton onClick={clearFilters}>
                    {t('common:actions.clearFilters')}
                  </ForgeButton>
                ) : null
              }
            />
          )}
        </>
      )}

      <UserEditorDrawer
        request={editor}
        onClose={() => setEditor(null)}
        onSaved={() => setEditor(null)}
      />

      {/* Deleting is soft on the server and has no undo on any screen, so the
          dialog says what it means rather than promising a recovery. Unlike a
          role, a person who owns work deletes cleanly — the records keep
          pointing at a user who no longer lists. See BACKEND-GAPS.md. */}
      <ForgeDialog
        open={Boolean(removing)}
        onOpenChange={(open) => (open ? null : setRemoving(null))}
        title={t('settings.users.deleteTitle')}
        description={t('settings.users.deleteBody', { name: removing?.name ?? '' })}
        footer={
          <>
            <ForgeButton onClick={() => setRemoving(null)}>
              {t('common:actions.cancel')}
            </ForgeButton>
            <ForgeButton
              variant="danger"
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate(removing.id, {
                  onSuccess: () => {
                    toast(t('settings.users.deleted', { name: removing.name }), 'ok')
                    setRemoving(null)
                  },
                  onError: () => toast(t('settings.users.deleteFailed'), 'error'),
                })
              }
            >
              {remove.isPending ? t('settings.users.deleting') : t('settings.users.deleteConfirm')}
            </ForgeButton>
          </>
        }
      />

      {/* Deactivating locks somebody out of everything at once, from a menu item
          sitting one row away from Edit — so it is confirmed, and the dialog says
          what is kept, because "deactivate" alone reads like "delete". */}
      <ForgeDialog
        open={Boolean(deactivating)}
        onOpenChange={(open) => (open ? null : setDeactivating(null))}
        title={t('settings.users.deactivateTitle')}
        description={t('settings.users.deactivateBody', { name: deactivating?.name ?? '' })}
        footer={
          <>
            <ForgeButton onClick={() => setDeactivating(null)}>
              {t('common:actions.cancel')}
            </ForgeButton>
            <ForgeButton
              variant="danger"
              disabled={setActive.isPending}
              onClick={() => applyActive(deactivating, false, () => setDeactivating(null))}
            >
              {setActive.isPending
                ? t('settings.users.deactivating')
                : t('settings.users.deactivateConfirm')}
            </ForgeButton>
          </>
        }
      />

      {/* <CreatePersonDialog open={creating} onClose={() => setCreating(false)} />
          <PeopleImportWizard open={importing} onClose={() => setImporting(false)} />
          Both replaced: the first by UserEditorDrawer, which invites over the
          real endpoint; the second by nothing, since there is no import API. */}
    </div>
  )
}

/**
 * What can be done to this account.
 *
 * Two states, so not three items: an active account can be deactivated and a
 * deactivated one reinstated. Offering "Activate" beside an already-active
 * account is offering a control that does nothing.
 *
 * Delete is here where the demo refused to have one — its reasoning was that
 * deleting somebody who owns tasks, shifts and handovers would orphan those
 * records with no undo beside it. The endpoint exists, the delete is soft, and
 * an account that should never have been created is a real thing to need gone.
 * Behind a confirmation that says so.
 */
function RowMenu({ person, pending, onEdit, onSetActive, onDelete }) {
  const { t } = useTranslation(['app', 'common'])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton bare label={t('settings.users.rowMenu', { name: person.name })}>
          <Icon name="list" size="sm" />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onEdit}>{t('common:actions.edit')}</DropdownMenuItem>
        <DropdownMenuItem disabled={pending} onSelect={() => onSetActive(!person.isActive)}>
          {t(person.isActive ? 'settings.users.deactivate' : 'settings.users.activate')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onDelete}>{t('settings.users.delete')}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function StatusPill({ active }) {
  const { t } = useTranslation('app')
  return (
    <span
      className={cn(
        'inline-flex w-fit items-center rounded-pill px-2.5 py-[3px] text-micro whitespace-nowrap',
        active ? cn(TINT_CLASS.green, ON_TINT_CLASS.green) : 'bg-sunken text-ink-dim',
      )}
    >
      {t(active ? 'settings.users.status.active' : 'settings.users.status.inactive')}
    </span>
  )
}

/** Says why this one row has no actions, rather than leaving the gap unexplained. */
function RootBadge() {
  const { t } = useTranslation('app')
  return (
    <span
      title={t('settings.users.rootHint')}
      className="shrink-0 rounded-pill bg-sunken px-2 py-[2px] text-micro whitespace-nowrap text-ink-dim"
    >
      {t('settings.users.rootBadge')}
    </span>
  )
}


// ══ NOT BUILT — THE OTHER TWO TABS ══════════════════════════════════════
// Commented out, not deleted, because neither has an endpoint: `/v1/teams`
// and `/v1/users/import` are fixture paths the API does not serve. Both are
// the design's tabs and both are wanted; restoring either is uncommenting
// what follows, putting its import back at the head of this file, and
// restoring the tab strip below it.
//
// Teams is the nearer of the two. §8.14's teams are a VIEW of who is in them
// — a person carries a `teamId` and the count, the sites, the lead and
// whether the team is active all fall out of it — and the live user record
// has no such field. So teams needs a resource of its own before this can
// read anything, which is what BACKEND-GAPS.md now asks for.
//
// Line comments throughout, including this banner: the code below carries its
// own block comments, and a `*/` inside one closes the outer one early. Which
// is exactly what the first draft of this banner did.
// ═══════════════════════════════════════════════════════════════════════

// ── The tab strip, and the state it switched ──────────────────────────────
// const TABS = ['users', 'teams', 'history']
// const TAB_ICON = { users: 'users', teams: 'factory', history: 'cal' }
//
// const ALL = 'all'
//
//
//       <div
//         role="tablist"
//         aria-label={t('settings.users.tabsLabel')}
//         className="mb-5 flex gap-1 border-b border-hairline"
//       >
//         {TABS.map((entry) => (
//           <button
//             key={entry}
//             type="button"
//             role="tab"
//             aria-selected={tab === entry}
//             onClick={() => setTab(entry)}
//             className={cn(
//               '-mb-px flex items-center gap-2 border-b-2 px-3.5 py-2.5 text-body-sm transition-colors',
//               tab === entry
//                 ? 'border-forge font-semibold text-ink'
//                 : 'border-transparent text-ink-dim hover:text-ink',
//             )}
//           >
//             <Icon name={TAB_ICON[entry]} size="sm" />
//             {t(`settings.users.tabs.${entry}`)}
//           </button>
//         ))}
//       </div>
//
//       {tab === 'users' ? <UsersTab /> : tab === 'teams' ? <TeamsTab /> : <ImportHistoryTab />}

// /* ══ TEAMS ═══════════════════════════════════════════════════════════════ */
//
// const TEAM_STATUS_TONE = { active: 'green', dormant: null }
//
// /**
//  * Groups, and what their membership implies.
//  *
//  * Everything but the name, the type and the channel is derived from who is in
//  * the team: the member count, the sites, whether the lead is still one of them,
//  * and whether the team is active at all. The design authors all four beside each
//  * other, so a team can report two members and list three.
//  */
// function TeamsTab() {
//   const { t } = useTranslation(['app', 'common'])
//
//   const [status, setStatus] = useState(ALL)
//   const [type, setType] = useState(ALL)
//   const [query, setQuery] = useState('')
//
//   const teams = useTeams({ status, type, q: query.trim() || undefined })
//
//   const rows = teams.data?.items ?? []
//   const stats = teams.data?.stats ?? {}
//
//   return (
//     <div>
//       <div className="mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-2.5">
//         <div className="min-w-0">
//           <h2 className="m-0 text-title">{t('settings.teams.management')}</h2>
//           <p className="mt-1 mb-0 text-body-sm text-ink-dim">{t('settings.teams.intro')}</p>
//         </div>
//         {/* No Create team. A team is a set of people carrying its id, so making
//             an empty one produces a row that reports nothing and cannot be
//             joined from here — membership is a property of the person. */}
//       </div>
//
//       {teams.isPending ? (
//         <SkeletonRows count={4} />
//       ) : teams.isError ? (
//         <ErrorState
//           error={teams.error}
//           title={t('settings.teams.loadFailed')}
//           onRetry={teams.refetch}
//         />
//       ) : (
//         <>
//           <div className="mb-3.5 grid grid-cols-4 gap-2.5 max-sm:grid-cols-2">
//             {['total', 'active', 'dormant', 'enterprise'].map((entry) => (
//               <div key={entry} className="rounded-sm border border-hairline bg-card px-3.5 py-3">
//                 <Num
//                   className={cn(
//                     'block text-display-sm',
//                     entry === 'active' && stats[entry] ? 'text-on-green' : 'text-ink',
//                   )}
//                 >
//                   {formatInteger(stats[entry] ?? 0)}
//                 </Num>
//                 <span className="eyebrow mt-1.5 block">{t(`settings.teams.facets.${entry}`)}</span>
//                 <span className="mt-0.5 block text-micro font-normal text-ink-mute">
//                   {t(`settings.teams.facetHints.${entry}`)}
//                 </span>
//               </div>
//             ))}
//           </div>
//
//           <Panel flush className="mb-3.5 px-3.5 py-2.5">
//             <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
//               <ForgeSelect
//                 id="teams-status"
//                 labelHidden
//                 size="sm"
//                 label={t('settings.teams.filterByStatus')}
//                 value={status}
//                 onValueChange={setStatus}
//                 className="w-[12rem] shrink-0"
//                 options={[
//                   { value: ALL, label: t('settings.teams.allStatuses') },
//                   { value: 'active', label: t('settings.teams.status.active') },
//                   { value: 'dormant', label: t('settings.teams.status.dormant') },
//                 ]}
//               />
//               <ForgeSelect
//                 id="teams-type"
//                 labelHidden
//                 size="sm"
//                 label={t('settings.teams.filterByType')}
//                 value={type}
//                 onValueChange={setType}
//                 className="w-[13rem] shrink-0"
//                 options={[
//                   { value: ALL, label: t('settings.teams.allTypes') },
//                   ...TEAM_TYPES.map((value) => ({
//                     value,
//                     label: t(`settings.teams.types.${value}`),
//                   })),
//                 ]}
//               />
//               <div className="ms-auto flex min-w-[13rem] items-center gap-2 rounded-sm border border-hairline bg-card px-3 py-1.5 focus-within:border-forge">
//                 <Icon name="search" size="sm" className="text-ink-mute" />
//                 <label className="sr-only" htmlFor="teams-search">
//                   {t('settings.teams.searchLabel')}
//                 </label>
//                 <input
//                   id="teams-search"
//                   value={query}
//                   onChange={(event) => setQuery(event.target.value)}
//                   placeholder={t('settings.teams.searchPlaceholder')}
//                   className="min-w-0 flex-1 border-0 bg-transparent text-body-sm text-ink outline-none placeholder:text-ink-dim"
//                 />
//               </div>
//               <p className="m-0 text-body-sm whitespace-nowrap text-ink-dim">
//                 <Num>
//                   {t('settings.users.matchCount', {
//                     shown: formatInteger(rows.length),
//                     total: formatInteger(stats.total ?? 0),
//                   })}
//                 </Num>
//               </p>
//             </div>
//           </Panel>
//
//           {rows.length ? (
//             <Panel flush className="overflow-hidden">
//               <div className="overflow-x-auto">
//                 <table className="w-full border-collapse">
//                   <caption className="sr-only">{t('settings.teams.tableCaption')}</caption>
//                   <thead>
//                     <tr>
//                       {['team', 'type', 'status', 'lead', 'members', 'sites', 'landing'].map(
//                         (column) => (
//                           <th
//                             key={column}
//                             scope="col"
//                             className={cn(
//                               'eyebrow bg-sunken px-3.5 py-2.5 whitespace-nowrap',
//                               column === 'members' ? 'text-end' : 'text-start',
//                             )}
//                           >
//                             {t(`settings.teams.columns.${column}`)}
//                           </th>
//                         ),
//                       )}
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {rows.map((team) => (
//                       <tr
//                         key={team.id}
//                         className={cn(
//                           'border-t border-subtle hover:bg-sunken',
//                           team.status === 'dormant' && STRIPE_CLASS.amber,
//                         )}
//                       >
//                         <td className="px-3.5 py-2.5">
//                           <span className="flex min-w-0 flex-col gap-0.5">
//                             <span className="text-body-sm font-semibold text-ink">{team.name}</span>
//                             <Mono className="text-micro text-ink-dim">{team.channel}</Mono>
//                           </span>
//                         </td>
//                         <td className="px-3.5 py-2.5 text-body-sm whitespace-nowrap text-ink-dim">
//                           {t(`settings.teams.types.${team.type}`)}
//                         </td>
//                         <td className="px-3.5 py-2.5">
//                           <span
//                             className={cn(
//                               'inline-flex w-fit items-center rounded-pill px-2.5 py-[3px] text-micro whitespace-nowrap',
//                               TEAM_STATUS_TONE[team.status]
//                                 ? cn(
//                                     TINT_CLASS[TEAM_STATUS_TONE[team.status]],
//                                     ON_TINT_CLASS[TEAM_STATUS_TONE[team.status]],
//                                   )
//                                 : 'bg-sunken text-ink-dim',
//                             )}
//                           >
//                             {t(`settings.teams.status.${team.status}`)}
//                           </span>
//                         </td>
//                         {/* A lead who has left the team is not a lead. The design
//                             lets a team name one who is not a member. */}
//                         <td className="px-3.5 py-2.5 text-body-sm whitespace-nowrap text-ink-dim">
//                           {team.leadName ?? (
//                             <span className="text-on-amber">{t('settings.teams.noLead')}</span>
//                           )}
//                         </td>
//                         <td className="px-3.5 py-2.5 text-end">
//                           <Num className="text-body-sm">{formatInteger(team.memberCount)}</Num>
//                         </td>
//                         <td className="px-3.5 py-2.5">
//                           <span className="flex min-w-0 flex-col gap-0.5">
//                             <span className="text-body-sm text-ink-dim">
//                               {t(`settings.users.scope.${team.scope}`)}
//                             </span>
//                             {team.siteNames.length ? (
//                               <span className="text-micro font-normal text-ink-mute">
//                                 {team.siteNames.join(' · ')}
//                               </span>
//                             ) : null}
//                           </span>
//                         </td>
//                         {/* A default page nobody can open is not a default page. */}
//                         <td className="px-3.5 py-2.5">
//                           <Link
//                             to={team.landing}
//                             className="text-body-sm text-ink-dim transition-colors hover:text-on-forge"
//                           >
//                             {t(`settings.teams.landing.${team.landingKey}`)}
//                           </Link>
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             </Panel>
//           ) : (
//             <EmptyState
//               icon="users"
//               title={t('settings.teams.noMatch')}
//               body={t('settings.teams.noMatchBody')}
//             />
//           )}
//         </>
//       )}
//     </div>
//   )
// }
//
// /* ══ IMPORT HISTORY ══════════════════════════════════════════════════════ */
//
// /**
//  * What bulk imports have been run.
//  *
//  * Empty to begin with — as the design's is, permanently, because nothing in the
//  * prototype ever writes to it. Here the wizard on the Users tab appends a row, so
//  * the tab is the record of something that happened.
//  */
// function ImportHistoryTab() {
//   const { t, i18n } = useTranslation(['app', 'common'])
//   const imports = usePeopleImports()
//
//   const rows = imports.data?.items ?? []
//
//   if (imports.isPending) return <SkeletonRows count={3} />
//   if (imports.isError) {
//     return (
//       <ErrorState
//         error={imports.error}
//         title={t('settings.imports.loadFailed')}
//         onRetry={imports.refetch}
//       />
//     )
//   }
//
//   if (!rows.length) {
//     return (
//       <EmptyState
//         icon="cal"
//         title={t('settings.imports.empty')}
//         body={t('settings.imports.emptyBody')}
//       />
//     )
//   }
//
//   return (
//     <Panel flush className="overflow-hidden">
//       <div className="overflow-x-auto">
//         <table className="w-full border-collapse">
//           <caption className="sr-only">{t('settings.imports.tableCaption')}</caption>
//           <thead>
//             <tr>
//               {['file', 'when', 'by', 'rows', 'created', 'updated', 'skipped'].map((column) => (
//                 <th
//                   key={column}
//                   scope="col"
//                   className={cn(
//                     'eyebrow bg-sunken px-3.5 py-2.5 whitespace-nowrap',
//                     ['rows', 'created', 'updated', 'skipped'].includes(column)
//                       ? 'text-end'
//                       : 'text-start',
//                   )}
//                 >
//                   {t(`settings.imports.columns.${column}`)}
//                 </th>
//               ))}
//             </tr>
//           </thead>
//           <tbody>
//             {rows.map((entry) => (
//               <tr key={entry.id} className="border-t border-subtle">
//                 <td className="px-3.5 py-2.5">
//                   <Mono className="text-body-sm text-ink">{entry.filename}</Mono>
//                 </td>
//                 <td className="px-3.5 py-2.5 text-body-sm whitespace-nowrap text-ink-dim">
//                   <time dateTime={entry.at}>
//                     {formatRelative(new Date(entry.at), i18n.language)}
//                   </time>
//                 </td>
//                 <td className="px-3.5 py-2.5 text-body-sm text-ink-dim">{entry.byName}</td>
//                 {['rows', 'created', 'updated', 'skipped'].map((key) => (
//                   <td key={key} className="px-3.5 py-2.5 text-end">
//                     <Num
//                       className={cn(
//                         'text-body-sm',
//                         key === 'skipped' && entry[key] ? 'font-semibold text-on-amber' : 'text-ink-dim',
//                       )}
//                     >
//                       {formatInteger(entry[key])}
//                     </Num>
//                   </td>
//                 ))}
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     </Panel>
//   )
// }
//
